import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { useAuth } from "../../lib/auth";

export const NAVE_NOTE = "Pagado con tarjeta online (NAVE): NAVE no permite devolver automáticamente. Hacé el reintegro en el portal de NAVE y después marcalo acá.";

export const useOffices = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => {
    if (user?.role === "admin") api.get("/agencies").then((r) => setList(Array.isArray(r.data) ? r.data.filter((a) => a.type !== "puntoenvio") : [])).catch(() => {});
  }, [user]);
  return { isAdmin: user?.role === "admin", list };
};

export const RefundMethod = ({ nave, pay, setPay, office, setOffice, testid }) => {
  const { isAdmin, list } = useOffices();
  if (nave) return <p data-testid={`${testid}-nave`} className="rounded-xl bg-amber-100 p-3 text-xs text-amber-950">{NAVE_NOTE}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      <select data-testid={`${testid}-pay`} value={pay} onChange={(e) => setPay(e.target.value)} className="h-9 rounded-lg border border-black/10 bg-white px-2 text-sm">
        <option value="efectivo">Devuelvo en efectivo</option><option value="transferencia">Devuelvo por transferencia</option><option value="tarjeta">Anulo en la tarjeta (posnet)</option>
      </select>
      {isAdmin && (
        <select data-testid={`${testid}-office`} value={office} onChange={(e) => setOffice(e.target.value)} className="h-9 min-w-0 rounded-lg border border-black/10 bg-white px-2 text-sm">
          <option value="">¿De qué caja sale?</option>
          {list.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.city}</option>)}
        </select>
      )}
    </div>
  );
};

export const RefundNow = ({ booking, onClose, onDone }) => {
  const { isAdmin } = useOffices();
  const [d, setD] = useState(null);
  const [pay, setPay] = useState("efectivo");
  const [office, setOffice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!booking) return undefined;
    setD(null);
    api.get(`/admin/bookings/${booking.id}/refund-quote`).then((r) => { setD(r.data); setPay(["efectivo", "tarjeta", "transferencia"].includes(r.data?.payment) ? r.data.payment : "efectivo"); })
      .catch((e) => { toast.error(apiError(e)); onClose(); });
    return undefined;
  }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async () => {
    if (!window.confirm(`¿Devolver ${formatARS(d.quote.amount)} y anular el pasaje ${d.code}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/refund`, { payment: d.nave ? null : pay, agency_id: office || null });
      toast.success(`Devolución de ${formatARS(data.amount)} registrada`);
      window.open(`/admin/devolucion/${data.id}`, "_blank");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={Boolean(booking)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="refund-now-dialog" className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Devolver pasaje</DialogTitle>
          <DialogDescription>{booking?.code} · {booking?.name} · butacas {booking?.seats?.join(", ")}</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            <p data-testid="refund-now-quote" className={`rounded-xl p-3 text-sm ${d.quote.amount ? "bg-[var(--lime)]/40" : "bg-red-50 text-red-900"}`}>{d.quote.reason}{d.quote.amount ? ` · devolver ${formatARS(d.quote.amount)} de ${formatARS(d.total)}` : ""}</p>
            {d.quote.amount > 0 && (
              <>
                {d.payment === "efectivo" && !d.nave && <p className="text-xs text-[var(--muted-ink)]">Se pagó en efectivo: la devolución es en el momento y sale de la caja.</p>}
                <RefundMethod nave={d.nave} pay={pay} setPay={setPay} office={office} setOffice={setOffice} testid="refund-now" />
                <button onClick={go} disabled={busy || (isAdmin && !d.nave && !office)} data-testid="refund-now-confirm" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white disabled:opacity-40">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} {d.nave ? "Ya lo devolví en NAVE: anular pasaje" : `Devolver ${formatARS(d.quote.amount)} e imprimir comprobante`}
                </button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
